import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteInstalledModel,
  downloadModel,
  getInstalledModel,
  isOpfsAvailable,
  type DownloadProgress,
  type ModelInfo,
} from "../data/modelStore";
import {
  resetCustomModel,
  setCustomModelFileName,
  setCustomModelUrl,
} from "../data/settingsStore";
import { effectiveModelConfig, type Settings } from "../data/types";
import { formatBytes } from "../ui/format";

interface Props {
  settings: Settings;
  onChange: (next: Settings) => void;
  onBack: () => void;
  onInstalledChange: () => void;
}

type InstallState =
  | { kind: "idle" }
  | { kind: "downloading"; progress: DownloadProgress }
  | { kind: "failed"; message: string };

export function ModelDownloadScreen(props: Props) {
  const config = effectiveModelConfig(props.settings);
  const [installed, setInstalled] = useState<ModelInfo | null>(null);
  const [state, setState] = useState<InstallState>({ kind: "idle" });
  const [opfsOk, setOpfsOk] = useState<boolean>(true);
  const abortRef = useRef<AbortController | null>(null);

  const refreshInstalled = useCallback(async () => {
    const info = await getInstalledModel(config.fileName);
    setInstalled(info);
    props.onInstalledChange();
  }, [config.fileName, props]);

  useEffect(() => {
    void isOpfsAvailable().then(setOpfsOk);
    void refreshInstalled();
  }, [refreshInstalled]);

  const handleInstall = async () => {
    if (state.kind === "downloading") return;
    const controller = new AbortController();
    abortRef.current = controller;
    setState({
      kind: "downloading",
      progress: { bytesDownloaded: 0, bytesTotal: null },
    });
    try {
      await downloadModel({
        url: config.url,
        fileName: config.fileName,
        authToken: props.settings.huggingFaceToken.trim() || undefined,
        signal: controller.signal,
        onProgress: (progress) => setState({ kind: "downloading", progress }),
      });
      setState({ kind: "idle" });
      await refreshInstalled();
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") {
        setState({ kind: "idle" });
        return;
      }
      const message = e instanceof Error ? e.message : String(e);
      setState({ kind: "failed", message });
    } finally {
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleDelete = async () => {
    if (!confirm("Delete the on-device model? You'll have to download it again.")) return;
    await deleteInstalledModel(config.fileName);
    await refreshInstalled();
    setState({ kind: "idle" });
  };

  const tokenMissing = props.settings.huggingFaceToken.trim().length === 0;
  const hasCustom =
    props.settings.customModelUrl.trim().length > 0 ||
    props.settings.customModelFileName.trim().length > 0;

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">On-device model</h1>
        <span className="w-16" />
      </header>

      <main className="flex flex-1 flex-col gap-5 px-4 py-4">
        <p className="text-sm text-ink-muted">
          Gemma runs entirely in your browser via WebGPU. Your raw ideas stay on this device unless
          you turn cloud LLM on. The model file is a few gigabytes and downloads once into OPFS
          (origin-private storage).
        </p>

        {!opfsOk && (
          <div className="rounded-xl bg-rose-50 p-3 text-sm text-rose-900 ring-1 ring-rose-200">
            <div className="font-semibold">OPFS isn't available in this browser.</div>
            <div className="mt-1">
              Try a recent Chrome, Edge, or Safari 18+ — Firefox is also fine in current versions.
            </div>
          </div>
        )}

        {tokenMissing && (
          <div className="rounded-xl bg-rose-50 p-3 text-sm text-rose-900 ring-1 ring-rose-200">
            <div className="font-semibold">Hugging Face token required</div>
            <div className="mt-1">
              Gemma is gated. Accept the license on huggingface.co for the model URL below, then
              paste a read token from huggingface.co/settings/tokens into the Hugging Face access
              section in Settings.
            </div>
          </div>
        )}

        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">Model source</h2>
          <p className="text-xs text-ink-muted">
            Defaults to Gemma 4 E2B (LiteRT-LM) on Hugging Face. Paste any /resolve/main/&lt;file&gt;
            URL plus the filename it should save as.
          </p>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-muted">Hugging Face resolve URL</span>
            <input
              className="input"
              placeholder="https://huggingface.co/<org>/<repo>/resolve/main/<file>.litertlm"
              value={props.settings.customModelUrl}
              disabled={state.kind === "downloading"}
              onChange={(e) => props.onChange(setCustomModelUrl(props.settings, e.target.value))}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-muted">Local filename</span>
            <input
              className="input"
              placeholder="e.g. gemma-4-E2B-it.litertlm"
              value={props.settings.customModelFileName}
              disabled={state.kind === "downloading"}
              onChange={(e) =>
                props.onChange(setCustomModelFileName(props.settings, e.target.value))
              }
            />
          </label>
          <div className="rounded-xl bg-surface p-3 text-xs ring-1 ring-black/5">
            <div className="font-semibold text-ink-muted">Will download</div>
            <div className="mt-1 break-all text-ink-muted">{config.url}</div>
            <div className="mt-1 text-ink-muted">Saved as: {config.fileName}</div>
          </div>
          {hasCustom && (
            <button
              className="btn-ghost self-start"
              disabled={state.kind === "downloading"}
              onClick={() => props.onChange(resetCustomModel(props.settings))}
            >
              Reset to default
            </button>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">Status</h2>
          <StatusCard state={state} installed={installed} />
        </section>

        <section className="flex flex-col gap-2">
          {state.kind === "downloading" ? (
            <button className="btn-ghost" onClick={handleCancel}>
              Cancel download
            </button>
          ) : installed ? (
            <button className="btn-danger" onClick={handleDelete}>
              Delete model
            </button>
          ) : (
            <button
              className="btn-primary"
              disabled={tokenMissing || !opfsOk}
              onClick={handleInstall}
            >
              {state.kind === "failed" ? "Retry download" : "Install model"}
            </button>
          )}
        </section>
      </main>
    </>
  );
}

function StatusCard(props: { state: InstallState; installed: ModelInfo | null }) {
  if (props.state.kind === "downloading") {
    const { bytesDownloaded, bytesTotal } = props.state.progress;
    const pct = bytesTotal ? Math.min(100, Math.round((bytesDownloaded / bytesTotal) * 100)) : null;
    return (
      <div className="rounded-xl bg-surface p-3 text-sm ring-1 ring-black/5">
        <div className="font-medium">
          Downloading… {pct !== null ? `${pct}%` : ""}
        </div>
        <div className="mt-1 text-xs text-ink-muted">
          {formatBytes(bytesDownloaded)}{bytesTotal ? ` of ${formatBytes(bytesTotal)}` : ""}
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/5">
          <div
            className="h-full bg-accent transition-[width]"
            style={{ width: pct !== null ? `${pct}%` : "20%" }}
          />
        </div>
      </div>
    );
  }
  if (props.state.kind === "failed") {
    return (
      <div className="rounded-xl bg-rose-50 p-3 text-sm text-rose-900 ring-1 ring-rose-200">
        <div className="font-semibold">Download failed</div>
        <div className="mt-1 whitespace-pre-wrap">{props.state.message}</div>
      </div>
    );
  }
  if (props.installed) {
    return (
      <div className="rounded-xl bg-accent-soft p-3 text-sm text-ink ring-1 ring-accent/20">
        <div className="font-semibold text-accent">
          Installed ({formatBytes(props.installed.sizeBytes)})
        </div>
        <div className="mt-1 text-xs text-ink-muted">{props.installed.fileName}</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-surface p-3 text-sm ring-1 ring-black/5">Not installed.</div>
  );
}
